import {
  Box,
  Button,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

interface Props {
  selectedCount: number;
  /** 選べる件数（検索結果のうち画面に出せる分） */
  selectableCount: number;
  /** 「すべて選択」の読み込み中か。ページを移ると選択の行は作り直されるため、画面の側で持つ */
  isSelectingAll: boolean;
  /** 出力中か。isSelectingAll と同じく画面の側で持つ */
  isExporting: boolean;
  onSelectAll: () => void;
  onClear: () => void;
  onExport: () => void;
}

/**
 * 法人マスタ一覧の上に置く選択の行。選んだ件数、すべて選択、選択の解除、Excel 出力を並べる
 * @param props.selectedCount 選んだ件数
 * @param props.selectableCount 選べる件数
 * @param props.isSelectingAll 「すべて選択」の読み込み中か
 * @param props.isExporting 出力中か
 * @param props.onSelectAll 検索結果をすべて選ぶ
 * @param props.onClear 選択を外す
 * @param props.onExport 選んだ法人を Excel に出す
 * @returns 選択の行
 */
export const CorporateSelectionBar: React.FC<Props> = ({
  selectedCount,
  selectableCount,
  isSelectingAll,
  isExporting,
  onSelectAll,
  onClear,
  onExport,
}) => {
  const allSelected = selectedCount >= selectableCount;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        flexWrap: "wrap",
        px: 1.5,
        py: 1,
        borderRadius: 1,
        bgcolor: "action.hover",
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
      >
        <Typography
          // 件数の表示（role="status"）は一覧の見出しが持つため、ここは読み上げだけ更新する
          aria-live="polite"
          variant="body2"
          fontWeight="bold"
        >
          {`${selectedCount.toLocaleString("ja-JP")}件を選択中`}
        </Typography>
        <Button
          size="small"
          disabled={allSelected || isSelectingAll}
          onClick={onSelectAll}
        >
          {`検索結果の${selectableCount.toLocaleString("ja-JP")}件をすべて選択`}
        </Button>
        <Button size="small" disabled={selectedCount === 0} onClick={onClear}>
          選択を解除
        </Button>
      </Stack>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Tooltip
          title="選んだ法人を Excel に出力します。法人住所・商業登記の取得状況など、一覧に無い項目も入ります。"
          arrow
          enterTouchDelay={0}
          leaveTouchDelay={5000}
        >
          <IconButton
            size="small"
            aria-label="Excel出力の説明"
            sx={{ width: 28, height: 28 }}
          >
            <HelpOutlineIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Button
          variant="contained"
          size="small"
          startIcon={<FileDownloadOutlinedIcon />}
          // 「すべて選択」の読み込み中は、選択がまだ前のままのため出させない
          disabled={selectedCount === 0 || isExporting || isSelectingAll}
          onClick={onExport}
        >
          {isExporting ? "出力中…" : "Excelで出力"}
        </Button>
      </Stack>
    </Box>
  );
};
